import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { HALF_W, HALF_H } from './constants';

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const DEG = Math.PI / 180;

// ─── CameraDirector ───────────────────────────────────────────────────────────
// 'tv'   — broadcast camera that follows the play (default)
// 'free' — user orbit / pan / zoom
// 'top'  — top-down tactical board view (zoom/pan still allowed)
export default class CameraDirector {
    constructor(camera, domElement, { onModeChange } = {}) {
        this.camera = camera;
        this.dom = domElement;
        this.onModeChange = onModeChange || (() => {});
        this.mode = 'tv';
        this._look = new THREE.Vector3(0, 0, 0);
        this._focus = new THREE.Vector3();
        this._focusVel = new THREE.Vector2();
        this._trans = null;
        this._fovTarget = camera.fov;

        this.controls = new OrbitControls(camera, domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.07;
        this.controls.minDistance = 5;
        this.controls.maxDistance = 90;
        this.controls.minPolarAngle = 0.01;
        this.controls.maxPolarAngle = Math.PI / 2 - 0.06;
        this.controls.enabled = false;

        // Any user gesture while the broadcast camera runs hands control to the user.
        // Capture phase so we run before OrbitControls' own pointerdown handler.
        this._onUserInput = () => {
            if (this.mode === 'tv') this._enterFree(false);
            else if (this._trans) this._finishTransition();
        };
        domElement.addEventListener('pointerdown', this._onUserInput, true);
        domElement.addEventListener('wheel', this._onUserInput, { capture: true, passive: true });

        this._snapTv = true;
    }

    get aspect() { return this.camera.aspect; }
    get portrait() { return this.camera.aspect < 1.1; }

    // ─── Presets ──────────────────────────────────────────────────────────────
    _tvShot(focus, vel) {
        const lead = { x: focus.x + clamp(vel.x, -8, 8) * 0.3, y: focus.y + clamp(vel.y, -8, 8) * 0.45 };
        if (this.portrait) {
            const fov = 56;
            const look = new THREE.Vector3(focus.x * 0.35, clamp(lead.y + 2.0, -HALF_H + 5.5, HALF_H - 5.5), 0.4);
            const hfov = 2 * Math.atan(Math.tan(fov * DEG / 2) * this.aspect);
            const dist = 8.6 / Math.tan(hfov / 2);
            const e = 0.9;
            const pos = look.clone().add(new THREE.Vector3(0, -Math.cos(e) * dist, Math.sin(e) * dist));
            return { pos, look, fov };
        }
        const fov = 34;
        const look = new THREE.Vector3(lead.x * 0.3, clamp(lead.y, -HALF_H + 7, HALF_H - 7), 0.5);
        const hfov = 2 * Math.atan(Math.tan(fov * DEG / 2) * this.aspect);
        const dist = clamp(11.5 / Math.tan(hfov / 2), 16, 34);
        const e = 0.55;
        const pos = new THREE.Vector3(look.x + Math.cos(e) * dist, look.y * 0.78, Math.sin(e) * dist);
        return { pos, look, fov };
    }

    _freePreset() {
        const fov = 50;
        const vFov = fov * DEG;
        const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.aspect);
        if (this.portrait) {
            const z = (HALF_W + 1) / Math.tan(hFov / 2) * 1.05;
            return { pos: new THREE.Vector3(0, -z * 0.55, z * 0.95), look: new THREE.Vector3(0, 1, 0), fov };
        }
        const d = (HALF_H + 1.5) / Math.tan(hFov / 2) * 1.05;
        return { pos: new THREE.Vector3(d * 0.85, -d * 0.2, d * 0.55), look: new THREE.Vector3(0, 0, 0), fov };
    }

    _topPreset() {
        const fov = 45;
        const vFov = fov * DEG;
        const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.aspect);
        const tilt = 0.015;
        if (this.portrait) {
            const dist = Math.max((HALF_W + 1.2) / Math.tan(hFov / 2), (HALF_H + 1.2) / Math.tan(vFov / 2));
            return { pos: new THREE.Vector3(0, -dist * Math.sin(tilt), dist * Math.cos(tilt)), look: new THREE.Vector3(), fov };
        }
        const dist = Math.max((HALF_H + 1.2) / Math.tan(hFov / 2), (HALF_W + 1.2) / Math.tan(vFov / 2));
        return { pos: new THREE.Vector3(dist * Math.sin(tilt), 0, dist * Math.cos(tilt)), look: new THREE.Vector3(), fov };
    }

    // ─── Mode switching ───────────────────────────────────────────────────────
    setMode(mode) {
        if (mode === 'tv') {
            this.controls.enabled = false;
            this._trans = null;
            this.mode = 'tv';
        } else if (mode === 'top') {
            this.mode = 'top';
            this._startTransition(this._topPreset());
        } else {
            this._enterFree(true);
            return;
        }
        this.onModeChange(this.mode);
    }

    // Re-frame the current mode's preset
    reset() {
        if (this.mode === 'tv') return;
        this._startTransition(this.mode === 'top' ? this._topPreset() : this._freePreset());
    }

    _enterFree(animate) {
        this.mode = 'free';
        if (animate) {
            this._startTransition(this._freePreset());
        } else {
            this._trans = null;
            this.controls.target.copy(this._look);
            this.controls.enabled = true;
            this.controls.update();
        }
        this.onModeChange(this.mode);
    }

    _startTransition(preset) {
        this.controls.enabled = false;
        this._trans = preset;
        this._fovTarget = preset.fov;
    }

    _finishTransition() {
        const t = this._trans;
        this.camera.position.copy(t.pos);
        this._look.copy(t.look);
        this.camera.lookAt(this._look);
        this.controls.target.copy(t.look);
        this.controls.enabled = true;
        this.controls.update();
        this._trans = null;
    }

    // ─── Per frame ────────────────────────────────────────────────────────────
    // focus: {x, y, vx, vy} — usually the puck
    update(dt, focus) {
        const cam = this.camera;
        if (this.mode === 'tv') {
            // Smoothed focus so the camera never jitters with stick-handling
            const kf = 1 - Math.exp(-4 * dt);
            this._focus.x += (focus.x - this._focus.x) * kf;
            this._focus.y += (focus.y - this._focus.y) * kf;
            this._focusVel.x += ((focus.vx || 0) - this._focusVel.x) * (1 - Math.exp(-1.5 * dt));
            this._focusVel.y += ((focus.vy || 0) - this._focusVel.y) * (1 - Math.exp(-1.5 * dt));
            const shot = this._tvShot(this._focus, this._focusVel);
            this._fovTarget = shot.fov;
            if (this._snapTv) {
                cam.position.copy(shot.pos);
                this._look.copy(shot.look);
                cam.fov = shot.fov;
                this._snapTv = false;
            } else {
                const k = 1 - Math.exp(-2.4 * dt);
                cam.position.lerp(shot.pos, k);
                this._look.lerp(shot.look, k);
            }
            cam.lookAt(this._look);
        } else if (this._trans) {
            const k = 1 - Math.exp(-5 * dt);
            cam.position.lerp(this._trans.pos, k);
            this._look.lerp(this._trans.look, k);
            cam.lookAt(this._look);
            if (cam.position.distanceTo(this._trans.pos) < 0.08) this._finishTransition();
        } else {
            this.controls.update();
            this._look.copy(this.controls.target);
        }

        if (Math.abs(cam.fov - this._fovTarget) > 0.01) {
            cam.fov += (this._fovTarget - cam.fov) * (1 - Math.exp(-5 * dt));
            cam.updateProjectionMatrix();
        }
    }

    // True when the camera looks almost straight down (hide the hanging scoreboard)
    isSteep() {
        const d = new THREE.Vector3().subVectors(this._look, this.camera.position).normalize();
        return d.z < -0.8;
    }

    resize(w, h) {
        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();
        if (this.mode === 'top') this._startTransition(this._topPreset());
    }

    dispose() {
        this.dom.removeEventListener('pointerdown', this._onUserInput, true);
        this.dom.removeEventListener('wheel', this._onUserInput, { capture: true });
        this.controls.dispose();
    }
}
